/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/

=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { NavLink, useSearchParams } from 'react-router-dom';
import React from 'react';
import axios from 'axios';
import { getBaseUrl } from 'utils/url';

// Chakra imports
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';

// Custom components
import CenteredAuth from 'layouts/auth/variants/Centered';

// Assets
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';

type ApiErrorShape = {
  code?: string;
  message?: string;
  details?: { companyName?: string };
};

function SignUp() {
  const [searchParams] = useSearchParams();
  const redirectAfterSignUp = searchParams.get('redirect');
  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorDetails = useColorModeValue('navy.700', 'secondaryGray.600');

  const {
    isOpen: isRequestAccessOpen,
    onOpen: onRequestAccessOpen,
    onClose: onRequestAccessClose,
  } = useDisclosure();
  const [registeredCompanyName, setRegisteredCompanyName] = React.useState('');
  const [requestAccessError, setRequestAccessError] =
    React.useState<ApiErrorShape | null>(null);
  const [requestAccessSuccessMessage, setRequestAccessSuccessMessage] =
    React.useState<string | null>(null);
  const [isRequestingAccess, setIsRequestingAccess] = React.useState(false);

  const [show, setShow] = React.useState(false);
  const [signUpError, setSignUpError] = React.useState<ApiErrorShape | null>(
    null,
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] =
    React.useState(false);
  const [formFields, setFormFields] = React.useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyDomain: '',
    industry: '',
    terms: false,
  });
  const [errors, setErrors] = React.useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyDomain: '',
    industry: '',
    terms: '',
  });

  const {
    email,
    password,
    firstName,
    lastName,
    companyName,
    companyDomain,
    industry,
    terms,
  } = formFields;

  const normalizeCompanyDomain = (raw: string) => {
    let s = raw.trim().toLowerCase();
    s = s.replace(/^https?:\/\//, '');
    s = s.split('/')[0] ?? '';
    return s;
  };

  const isValidCompanyDomain = (domain: string) => {
    if (!domain) return false;
    // e.g. example.com, sub.example.co.uk (no path, no @)

    return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(
      domain,
    );
  };

  const industries = React.useMemo(
    () => [
      'Technology',
      'Healthcare',
      'Financial Services',
      'Manufacturing',
      'Retail',
      'Education',
      'Real Estate',
      'Consulting',
      'Media & Entertainment',
      'Transportation',
      'Energy',
      'Government',
      'Non-Profit',
      'Agriculture',
      'Construction',
      'Hospitality',
      'Legal',
      'Insurance',
      'Telecommunications',
      'Other',
    ],
    [],
  );

  const handleClick = () => setShow(!show);

  const uri = `${getBaseUrl()}/api/auth/sign-up`;
  const requestMembershipUri = `${getBaseUrl()}/api/auth/request-membership-access`;

  const handleRequestAccessModalClose = () => {
    setRequestAccessError(null);
    setRequestAccessSuccessMessage(null);
    setRegisteredCompanyName('');
    onRequestAccessClose();
  };

  const handleRequestMembershipAccess = async () => {
    if (!email?.trim() || !password) {
      setRequestAccessError({
        message: 'Email and password are required.',
      });
      return;
    }
    if (password.length < 8) {
      setRequestAccessError({
        message: 'Password must be at least 8 characters.',
      });
      return;
    }

    setIsRequestingAccess(true);
    setRequestAccessError(null);

    try {
      const res = await axios.post(
        requestMembershipUri,
        {
          email: email.trim(),
          password,
          firstName,
          lastName,
          companyName,
          industry,
        },
        { withCredentials: true },
      );
      setRequestAccessSuccessMessage(
        typeof res.data?.message === 'string'
          ? res.data.message
          : 'Your request was sent. You will be able to sign in after an organization admin approves your access.',
      );
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: ApiErrorShape } };
      };
      const apiError = axiosErr.response?.data?.error;
      setRequestAccessError(
        apiError ?? { message: 'Request failed. Try again.' },
      );
    } finally {
      setIsRequestingAccess(false);
    }
  };

  const handleSignUp = (e: any) => {
    e.preventDefault();
    const domainNormalized = normalizeCompanyDomain(companyDomain);

    const nextErrors = {
      email: !email ? 'Email is required' : '',
      password: '',
      firstName: !firstName ? 'First name is required' : '',
      lastName: !lastName ? 'Last name is required' : '',
      companyName: !companyName ? 'Company name is required' : '',
      companyDomain: '',
      industry: !industry ? 'Industry is required' : '',
      terms: !terms ? 'Terms and conditions are required' : '',
    };

    if (!domainNormalized) {
      nextErrors.companyDomain = 'Company domain is required';
    } else if (!isValidCompanyDomain(domainNormalized)) {
      nextErrors.companyDomain = 'Enter a valid domain (e.g. yourcompany.com)';
    }

    if (!password) {
      nextErrors.password = 'Password is required';
    } else if (password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
    }

    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }

    setIsLoading(true);
    setSignUpError(null);

    FirebaseAnalyticsService.trackFeatureUsage('Auth', 'sign_up_started');

    axios
      .post(
        uri,
        {
          email,
          password,
          firstName,
          lastName,
          companyName,
          companyDomain: domainNormalized,
          industry,
        },
        { withCredentials: true },
      )
      .then(async (res) => {
        const auth = getAuth();
        await signInWithCustomToken(auth, res.data.customToken);
        FirebaseAnalyticsService.trackFeatureUsage('Auth', 'sign_up_success');

        const next =
          redirectAfterSignUp && redirectAfterSignUp.startsWith('/')
            ? redirectAfterSignUp
            : '/admin/home';
        window.location.href = next;
      })
      .catch((err) => {
        const status = err.response?.status;
        const apiError: ApiErrorShape | undefined = err.response?.data?.error;
        if (status === 409 && apiError?.code === 'COMPANY_ALREADY_REGISTERED') {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Auth',
            'sign_up_company_exists',
          );
          setSignUpError(null);
          setRegisteredCompanyName(apiError.details?.companyName?.trim() || '');
          setRequestAccessError(null);
          setRequestAccessSuccessMessage(null);
          onRequestAccessOpen();
          return;
        }
        FirebaseAnalyticsService.trackFeatureUsage('Auth', 'sign_up_failed', {
          error_code: apiError?.code || 'unknown',
          status,
        });
        setSignUpError(
          apiError ?? { message: 'An error occurred. Try again.' },
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (field: string, value: string | boolean) => {
    setErrors({
      ...errors,
      [field]: '',
    });
    setFormFields({
      ...formFields,
      [field]: value,
    });
  };

  return (
    <>
      <form onSubmit={handleSignUp} noValidate>
        <CenteredAuth
          cardTop={{ base: '140px', md: '20vh' }}
          cardBottom={{ base: '50px', lg: '100px' }}
        >
          <Flex
            maxW="max-content"
            mx={{ base: 'auto', lg: '0px' }}
            me="auto"
            justifyContent="center"
            px={{ base: '20px', md: '0px' }}
            flexDirection="column"
          >
            <Box me="auto">
              <Heading
                color={textColor}
                fontSize={{ base: '34px', lg: '36px' }}
                mb="10px"
              >
                Sign Up
              </Heading>
              <Text
                mb="36px"
                ms="4px"
                color={textColorSecondary}
                fontWeight="400"
                fontSize="md"
              >
                Enter your email and password to sign up!
              </Text>
            </Box>
            <Flex
              zIndex="2"
              direction="column"
              w={{ base: '100%', md: '420px' }}
              maxW="100%"
              background="transparent"
              borderRadius="15px"
              mx={{ base: 'auto', lg: 'unset' }}
              me="auto"
              mb={{ base: '20px', md: 'auto' }}
            >
              {signUpError && (
                <Alert status="error" mb="24px" borderRadius="15px">
                  <AlertIcon />
                  {signUpError?.message || 'An error occurred. Try again.'}
                </Alert>
              )}
              <FormControl isInvalid={!!errors.companyName} mb="24px">
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Company Name<Text color={textColor}>*</Text>
                </FormLabel>
                <Input
                  isRequired={true}
                  isInvalid={!!errors.companyName}
                  variant="auth"
                  fontSize="sm"
                  placeholder="Company name"
                  size="lg"
                  onChange={({ target: { value } }) =>
                    handleChange('companyName', value)
                  }
                />
                <FormErrorMessage ml="12px" mt="8px">
                  {errors.companyName}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.companyDomain} mb="24px">
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Company domain<Text color={textColor}>*</Text>
                </FormLabel>
                <Input
                  isRequired={true}
                  isInvalid={!!errors.companyDomain}
                  variant="auth"
                  fontSize="sm"
                  placeholder="yourcompany.com"
                  size="lg"
                  value={companyDomain}
                  onChange={({ target: { value } }) =>
                    handleChange('companyDomain', value)
                  }
                />
                <FormErrorMessage ml="12px" mt="8px">
                  {errors.companyDomain}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.industry} mb="24px">
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Industry<Text color={textColor}>*</Text>
                </FormLabel>
                <Box position="relative">
                  <Box position="relative">
                    <Input
                      isRequired={true}
                      isInvalid={!!errors.industry}
                      variant="auth"
                      fontSize="sm"
                      size="lg"
                      value={industry}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleChange('industry', value);
                      }}
                      onFocus={() => setIsIndustryDropdownOpen(true)}
                      onBlur={() => {
                        // Delay closing to allow clicking on dropdown items
                        setTimeout(() => setIsIndustryDropdownOpen(false), 200);
                      }}
                      placeholder="Select industry"
                    />
                    <Box
                      position="absolute"
                      right="12px"
                      top="50%"
                      transform="translateY(-50%)"
                      pointerEvents="none"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6,9 12,15 18,9"></polyline>
                      </svg>
                    </Box>
                  </Box>
                  {isIndustryDropdownOpen && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid #E2E8F0"
                      borderRadius="8px"
                      maxH="200px"
                      overflowY="auto"
                      zIndex={1000}
                      boxShadow="lg"
                      mt={1}
                    >
                      {industries.map((industryOption) => (
                        <Box
                          key={industryOption}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                          onClick={() => {
                            handleChange('industry', industryOption);
                            setIsIndustryDropdownOpen(false);
                          }}
                        >
                          <Text>{industryOption}</Text>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <FormErrorMessage ml="12px" mt="8px">
                  {errors.industry}
                </FormErrorMessage>
              </FormControl>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                gap={{ sm: '10px', md: '26px' }}
              >
                <Flex direction="column">
                  <FormControl isInvalid={!!errors.firstName} mb="24px">
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px"
                    >
                      First name<Text color={textColor}>*</Text>
                    </FormLabel>
                    <Input
                      isRequired={true}
                      isInvalid={!!errors.firstName}
                      fontSize="sm"
                      ms={{ base: '0px', md: '4px' }}
                      placeholder="First name"
                      variant="auth"
                      size="lg"
                      onChange={({ target: { value } }) =>
                        handleChange('firstName', value)
                      }
                    />
                    <FormErrorMessage ml="12px" mt="8px">
                      {errors.firstName}
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
                <Flex direction="column">
                  <FormControl isInvalid={!!errors.lastName} mb="24px">
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px"
                    >
                      Last name<Text color={textColor}>*</Text>
                    </FormLabel>
                    <Input
                      isRequired={true}
                      isInvalid={!!errors.lastName}
                      variant="auth"
                      fontSize="sm"
                      placeholder="Last name"
                      size="lg"
                      onChange={({ target: { value } }) =>
                        handleChange('lastName', value)
                      }
                    />
                    <FormErrorMessage ml="12px" mt="8px">
                      {errors.lastName}
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
              </SimpleGrid>
              <FormControl isInvalid={!!errors.email} mb="24px">
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Email<Text color={textColor}>*</Text>
                </FormLabel>
                <Input
                  isRequired={true}
                  isInvalid={!!errors.email}
                  variant="auth"
                  fontSize="sm"
                  type="email"
                  placeholder="mail@simmmple.com"
                  size="lg"
                  onChange={({ target: { value } }) =>
                    handleChange('email', value)
                  }
                />
                <FormErrorMessage ml="12px" mt="8px">
                  {errors.email}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.password} mb="24px">
                <FormLabel
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  display="flex"
                >
                  Password<Text color={textColor}>*</Text>
                </FormLabel>
                <InputGroup size="md">
                  <Input
                    isRequired={true}
                    isInvalid={!!errors.password}
                    variant="auth"
                    fontSize="sm"
                    ms={{ base: '0px', md: '4px' }}
                    placeholder="Min. 8 characters"
                    size="lg"
                    type={show ? 'text' : 'password'}
                    onChange={({ target: { value } }) =>
                      handleChange('password', value)
                    }
                  />
                  <InputRightElement
                    display="flex"
                    alignItems="center"
                    mt="4px"
                  >
                    <Icon
                      color={textColorSecondary}
                      _hover={{ cursor: 'pointer' }}
                      as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                      onClick={handleClick}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage ml="12px" mt="8px">
                  {errors.password}
                </FormErrorMessage>
              </FormControl>
              <Flex justifyContent="space-between" align="center" mb="24px">
                <Checkbox
                  id="remember-login"
                  colorScheme="brand"
                  me="10px"
                  mt="3px"
                  isInvalid={!!errors.terms}
                  onChange={({ target: { checked } }) =>
                    handleChange('terms', checked)
                  }
                />
                <FormControl display="flex" alignItems="start">
                  <FormLabel
                    htmlFor="remember-login"
                    mb="0"
                    fontWeight="normal"
                    color={textColor}
                    fontSize="sm"
                  >
                    By creating an account means you agree to the{' '}
                    <Link
                      href="https://cocorev.com/terms-and-conditions"
                      fontWeight="600"
                      target="_blank"
                    >
                      Terms and Conditions,
                    </Link>{' '}
                    and our{' '}
                    <Link
                      href="https://cocorev.com/gdpr-policy"
                      fontWeight="600"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                  </FormLabel>
                  <FormErrorMessage ml="12px" mt="8px">
                    {errors.terms}
                  </FormErrorMessage>
                </FormControl>
              </Flex>
              <Button
                variant="brand"
                fontSize="14px"
                fontWeight="500"
                w="100%"
                h="50"
                mb="24px"
                type="submit"
                isLoading={isLoading}
                loadingText="Creating Account..."
                disabled={isLoading}
              >
                {isLoading ? (
                  <Flex align="center" gap="2">
                    <Spinner size="sm" color="white" />
                    Creating Account...
                  </Flex>
                ) : (
                  'Create my account'
                )}
              </Button>
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="start"
                maxW="100%"
                mt="0px"
              >
                <Text color={textColorDetails} fontWeight="400" fontSize="sm">
                  Already a member?
                  <NavLink to="/auth/sign-in">
                    <Text color="black" as="span" ms="5px" fontWeight="700">
                      Sign in
                    </Text>
                  </NavLink>
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </CenteredAuth>
      </form>

      <Modal
        isOpen={isRequestAccessOpen}
        onClose={handleRequestAccessModalClose}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent mx={{ base: '16px', md: undefined }}>
          <ModalHeader pr="48px">
            Request access to your organization
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {requestAccessSuccessMessage ? (
              <Flex direction="column" gap={4}>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="600" mb={1}>
                      Request sent
                    </Text>
                    <Text fontSize="sm">{requestAccessSuccessMessage}</Text>
                  </Box>
                </Alert>
                <Text fontSize="sm" color={textColorSecondary}>
                  You will not be able to login until an admin approves you.
                  After approval, sign in with the email and password you used
                  here.
                </Text>
              </Flex>
            ) : (
              <Flex direction="column" gap={4}>
                <Text fontSize="sm" color={textColor}>
                  {registeredCompanyName ? (
                    <>
                      An organization (
                      <Text as="span" fontWeight="600">
                        {registeredCompanyName}
                      </Text>
                      ) already has an account. Request access to join their
                      organization.
                    </>
                  ) : (
                    <>
                      An organization is already registered for your email
                      domain. Submit a request and a tenant admin can approve
                      your access.
                    </>
                  )}
                </Text>
                {requestAccessError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Box flex="1">
                      <Text fontSize="sm">
                        {requestAccessError.message ||
                          'Request could not be sent.'}
                      </Text>
                      {requestAccessError.code === 'USER_EXISTS' && (
                        <Text fontSize="sm" mt={2}>
                          <NavLink to="/auth/sign-in">
                            <Text as="span" color="brand.500" fontWeight="600">
                              Sign in
                            </Text>
                          </NavLink>{' '}
                          if you already have an account, or use forgot password
                          on the sign-in page.
                        </Text>
                      )}
                    </Box>
                  </Alert>
                )}
              </Flex>
            )}
          </ModalBody>
          <ModalFooter gap={3} flexWrap="wrap">
            {requestAccessSuccessMessage ? (
              <Button variant="brand" onClick={handleRequestAccessModalClose}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={handleRequestAccessModalClose}>
                  Cancel
                </Button>
                <Button
                  variant="brand"
                  onClick={() => void handleRequestMembershipAccess()}
                  isLoading={isRequestingAccess}
                  loadingText="Sending..."
                >
                  Request access
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default SignUp;
